import {trackAmplitudeEvent, whenAmplitudeReady} from '@udemy-v2/amplitude';
import {TrackingEvent} from '@udemy-v2/event-tracking';

import {TeamPlanLeadFormSubmissionFailedEvent} from '@/features/organization-team-plan/utils/amplitude/team-plan-lead-form-submission-failed-event';
import {TeamPlanLeadFormInteractedEvent} from '@/features/organization-team-plan/utils/amplitude/team-plan-lead-form-interacted-event';
import {TeamPlanLeadFormPresentedEvent} from '@/features/organization-team-plan/utils/amplitude/team-plan-lead-form-presented-event';
import {TeamPlanLeadFormSubmittedEvent} from '@/features/organization-team-plan/utils/amplitude/team-plan-lead-form-submitted-event';
import {TeamPlanLeadFormSubmissionSucceededEvent} from '@/features/organization-team-plan/utils/amplitude/team-plan-lead-form-submission-succeeded-event';

export const TEAM_PLAN_LEAD_FORM_TRACKING_TYPE = {
    presented: 'TeamPlanLeadFormPresented',
    interacted: 'TeamPlanLeadFormInteracted',
    submitted: 'TeamPlanLeadFormSubmitted',
    submissionSucceeded: 'TeamPlanLeadFormSubmissionSucceeded',
    submissionFailed: 'TeamPlanLeadFormSubmissionFailed',
} as const;

export interface TeamPlanLeadFormEventTracker {
    publishEvent(event: TrackingEvent): boolean;
}

function publishToCollector(tracker: TeamPlanLeadFormEventTracker, type: string, data: Record<string, unknown>) {
    tracker.publishEvent(new TrackingEvent(type, data));
}

/** Form presented to the user — collector + Amplitude. */
export function publishTeamPlanLeadFormPresented(tracker: TeamPlanLeadFormEventTracker, formId: string) {
    publishToCollector(tracker, TEAM_PLAN_LEAD_FORM_TRACKING_TYPE.presented, {formId});
    whenAmplitudeReady(() => trackAmplitudeEvent(new TeamPlanLeadFormPresentedEvent(formId)));
}

/** First field interaction — collector + Amplitude. */
export function publishTeamPlanLeadFormInteracted(tracker: TeamPlanLeadFormEventTracker, formId: string) {
    publishToCollector(tracker, TEAM_PLAN_LEAD_FORM_TRACKING_TYPE.interacted, {formId});
    whenAmplitudeReady(() => trackAmplitudeEvent(new TeamPlanLeadFormInteractedEvent(formId)));
}

/** Submit attempted — collector + Amplitude. */
export function publishTeamPlanLeadFormSubmitted(tracker: TeamPlanLeadFormEventTracker, formId: string) {
    publishToCollector(tracker, TEAM_PLAN_LEAD_FORM_TRACKING_TYPE.submitted, {formId});
    whenAmplitudeReady(() => trackAmplitudeEvent(new TeamPlanLeadFormSubmittedEvent(formId)));
}

/** Submit succeeded — collector + Amplitude. */
export function publishTeamPlanLeadFormSubmissionSucceeded(tracker: TeamPlanLeadFormEventTracker, formId: string) {
    publishToCollector(tracker, TEAM_PLAN_LEAD_FORM_TRACKING_TYPE.submissionSucceeded, {formId});
    whenAmplitudeReady(() => trackAmplitudeEvent(new TeamPlanLeadFormSubmissionSucceededEvent(formId)));
}

/** Submit failed — collector + Amplitude. */
export function publishTeamPlanLeadFormSubmissionFailed(
    tracker: TeamPlanLeadFormEventTracker,
    formId: string,
    failureReason: string,
) {
    publishToCollector(tracker, TEAM_PLAN_LEAD_FORM_TRACKING_TYPE.submissionFailed, {
        formId,
        failureReason,
    });
    whenAmplitudeReady(() => trackAmplitudeEvent(new TeamPlanLeadFormSubmissionFailedEvent(formId, failureReason)));
}
